import { useCallback } from 'react';

import { Box, alpha, Typography, makeStyles } from '@material-ui/core';
import { ArrowForwardIos } from '@material-ui/icons';
import { useModal } from 'react-modal-hook';

import { MediaTypeConfig } from 'constants/mediaTypes';

import DrawerMidiasContato from './DrawerMidiasContato';

const useStyles = makeStyles(({ spacing, palette }) => ({
  file: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    borderRadius: spacing(1),
    cursor: 'pointer',
    padding: spacing(1.5),
    transition: 'background-color 0.1s ease',

    '&:hover': {
      backgroundColor: alpha(palette.background.default, 0.7),
    },
  },
  fileContainer: {
    gap: spacing(1.2),
  },
  iconContainer: {
    width: 50,
    height: 50,
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

interface FileCategoryProps {
  contatoId: string;
  canalId: string;
  mediaType: MediaTypeConfig;
  color: string;
  /**
   * Quando fornecido, delega a abertura do drawer ao pai (ex: modal inline).
   * Quando omitido, usa o comportamento padrão via useModal (drawer global).
   */
  onOpen?: (mediaType: MediaTypeConfig) => void;
}

export default function FileCategory({
  contatoId,
  canalId,
  mediaType,
  color,
  onOpen,
}: FileCategoryProps) {
  const classes = useStyles();
  const IconComponent = mediaType.icon;

  // Fallback: drawer global via react-modal-hook (usado quando onOpen não é fornecido)
  const [showDrawer, hideDrawer] = useModal(
    ({ in: open }) => (
      <DrawerMidiasContato
        open={open}
        onClose={hideDrawer}
        contatoId={contatoId}
        canalId={canalId}
        mediaType={mediaType}
      />
    ),
    [contatoId, canalId, mediaType],
  );

  const handleClick = useCallback(() => {
    if (!canalId) return;

    if (onOpen) {
      onOpen(mediaType);
    } else {
      showDrawer();
    }
  }, [canalId, mediaType, onOpen, showDrawer]);

  return (
    <Box
      className={classes.file}
      onClick={handleClick}
      style={{
        opacity: canalId ? 1 : 0.5,
        cursor: canalId ? 'pointer' : 'not-allowed',
      }}
    >
      <Box display="flex" alignItems="center" className={classes.fileContainer}>
        <Box
          className={classes.iconContainer}
          style={{
            backgroundColor: alpha(color, 0.1),
            color,
          }}
        >
          <IconComponent />
        </Box>

        <Typography
          variant="body2"
          color="textPrimary"
          style={{ fontWeight: 500 }}
        >
          {mediaType.title}
        </Typography>
      </Box>

      <ArrowForwardIos style={{ width: 16, height: 16 }} />
    </Box>
  );
}
